import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import router from 'next/router';
import { setCookie } from 'cookies-next';
import { PaginatedResponse } from 'types/PaginatedResponse';

interface CastWarning {
  code?: string;
  message?: string;
  included?: any;
}

interface CASTAxiosResponse<T = object | string | any[]> extends AxiosResponse<T> {
  pagination: PaginatedResponse['pagination'] | null;
  warnings?: CastWarning[];
}

export interface CASTAxiosError {
  data: any;
}

const isAbsoluteURL = /^(?:\w+:)\/\//;

export class Http {
  private readonly baseUrlOverride: string = null;
  private readonly token: string = null;
  private readonly tenantName: string = null;
  private instance: AxiosInstance | null = null;

  protected get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp();
  }

  constructor(baseUrlOverride = '', token?, tenantName?) {
    this.baseUrlOverride = baseUrlOverride;
    this.token = token ?? null;
    this.tenantName = tenantName ?? null;
  }

  private initHttp() {
    const http = axios.create({
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    http.interceptors.request.use(
      (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        if (this.baseUrlOverride) {
          if (!isAbsoluteURL.test(config.url)) {
            config.url = `${this.baseUrlOverride}${config.url}`;
          }
        }
        if (this.token !== null) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        if (!config.headers['Tenant-Name']) {
          config.headers['Tenant-Name'] = router?.query?.tenantName || this.tenantName;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    http.interceptors.response.use(
      (response: AxiosResponse): CASTAxiosResponse => {
        const transformedResponse: CASTAxiosResponse = {
          ...response,
          pagination: null,
          warnings: [],
        };
        if (response.data.pagination) {
          transformedResponse.pagination = response.data.pagination;
        }
        if (response.data.data) {
          transformedResponse.data = response.data.data;
        }
        if (response.data.warnings) {
          transformedResponse.warnings = response.data.warnings || [];
        }
        if (response.headers['x-cookie-expiration']) {
          setCookie('PublicPortalAuthExpiration', response.headers['x-cookie-expiration']);
        }

        return transformedResponse;
      },
      (error): Promise<void | CASTAxiosError> => {
        if (!axios.isCancel(error)) {
          console.error('error response from api wrapper', error);
          return Promise.reject(error.response);
        }
      }
    );

    this.instance = http;
    return http;
  }

  // config can also be passed from calling function, but it defined here with session storage.

  get<T>(url: string, config?: AxiosRequestConfig): Promise<CASTAxiosResponse<T>> {
    return this.http.get<T, CASTAxiosResponse<T>>(url, config);
  }

  post<T, Payload = any>(
    url: string,
    data?: Payload,
    config?: AxiosRequestConfig
  ): Promise<CASTAxiosResponse<T>> {
    return this.http.post<T, CASTAxiosResponse<T>, Payload>(url, data, config);
  }

  put<T, Payload = any>(
    url: string,
    data?: Payload,
    config?: AxiosRequestConfig
  ): Promise<CASTAxiosResponse<T>> {
    return this.http.put<T, CASTAxiosResponse<T>, Payload>(url, data, config);
  }

  patch<T, Payload = any>(
    url: string,
    data?: Payload,
    config?: AxiosRequestConfig
  ): Promise<CASTAxiosResponse<T>> {
    return this.http.patch<T, CASTAxiosResponse<T>, Payload>(url, data, config);
  }

  delete<T, Payload = any>(
    url: string,
    data?: Payload,
    config?: AxiosRequestConfig
  ): Promise<CASTAxiosResponse<T>> {
    if (data) {
      if (config) {
        config.data = data;
      } else {
        config = { data };
      }
    }
    return this.http.delete<T, CASTAxiosResponse<T>, Payload>(url, config);
  }
}

export const http = new Http();
